import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  AlertCircle,
  Target,
  Users,
  Leaf,
  Shield,
  Database,
  FileText,
  ChevronRight,
  Play,
  Check,
  X,
  RefreshCw,
  Sparkles,
  Filter,
  BarChart3,
  TrendingUp,
  Zap,
  ExternalLink,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { API } from '../App';
import { apiFetch } from '../services/api_service';


const ActionCenter = () => {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterPriority, setFilterPriority] = useState('all');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchActions();
  }, [filterStatus, filterPriority]);

  const fetchActions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      
      const response = await fetch(`${API}/action-center?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      setActions(data.actions || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
    setLoading(false);
  };

  const generateSmartActions = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/action-center/generate-smart`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      await fetchActions();
    } catch (error) {
      console.error('Error generating actions:', error);
    }
    setGenerating(false);
  };

  const updateActionStatus = async (actionId, status) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/action-center/${actionId}/${status}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      await fetchActions();
    } catch (error) {
      console.error('Error updating action:', error);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      compliance_deadline: Shield,
      compliance_gap: AlertTriangle,
      supplier_outreach: Users,
      carbon_offset: Leaf,
      risk_mitigation: AlertCircle,
      data_quality: Database,
      data_collection: FileText,
      emission_reduction: TrendingUp,
      general: Target
    };
    return icons[type] || Target;
  };

  const getTypeColor = (type) => {
    const colors = {
      compliance_deadline: 'text-red-600 bg-red-100',
      compliance_gap: 'text-orange-600 bg-orange-100',
      supplier_outreach: 'text-blue-600 bg-blue-100',
      carbon_offset: 'text-emerald-600 bg-emerald-100',
      risk_mitigation: 'text-purple-600 bg-purple-100',
      data_quality: 'text-amber-600 bg-amber-100',
      emission_reduction: 'text-teal-600 bg-teal-100'
    };
    return colors[type] || 'text-slate-600 bg-slate-100';
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { bg: 'bg-red-500', text: 'text-white', label: 'CRITICAL' },
      high: { bg: 'bg-orange-500', text: 'text-white', label: 'HIGH' },
      medium: { bg: 'bg-yellow-500', text: 'text-white', label: 'MEDIUM' },
      low: { bg: 'bg-blue-500', text: 'text-white', label: 'LOW' }
    };
    return configs[priority] || configs.medium;
  };

  const handleActionClick = (action, suggestedAction) => {
    if (suggestedAction.url) {
      navigate(suggestedAction.url);
    }
  };

  return (
    <div className="space-y-6" data-testid="action-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Obligations & Remediation</h1>
            <p className="text-sm text-slate-500">Unified hub for all platform tasks and recommendations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={generateSmartActions}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            data-testid="generate-smart-btn"
          >
            <Sparkles size={16} className={generating ? 'animate-pulse' : ''} />
            {generating ? 'Generating...' : 'Generate Smart Actions'}
          </button>
          <button
            onClick={fetchActions}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle size={20} className="text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.by_priority?.critical || 0}</div>
              <div className="text-xs text-slate-500">Critical</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle size={20} className="text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.by_priority?.high || 0}</div>
              <div className="text-xs text-slate-500">High Priority</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total_pending || 0}</div>
              <div className="text-xs text-slate-500">Pending</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Play size={20} className="text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total_in_progress || 0}</div>
              <div className="text-xs text-slate-500">In Progress</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total_completed || 0}</div>
              <div className="text-xs text-slate-500">Completed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filters:</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Status:</span>
          <div className="flex gap-1">
            {['all', 'pending', 'in_progress', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filterStatus === status
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-status-${status}`}
              >
                {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Priority:</span>
          <div className="flex gap-1">
            {['all', 'critical', 'high', 'medium', 'low'].map(priority => (
              <button
                key={priority}
                onClick={() => setFilterPriority(priority)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filterPriority === priority
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-priority-${priority}`}
              >
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw size={32} className="mx-auto text-slate-400 animate-spin" />
            <p className="mt-2 text-slate-500">Loading actions...</p>
          </div>
        ) : actions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <CheckCircle size={48} className="mx-auto text-emerald-500" />
            <h3 className="mt-4 text-lg font-medium text-slate-900">All Caught Up!</h3>
            <p className="text-slate-500">No actions matching your filters.</p>
            <button
              onClick={generateSmartActions}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Generate Smart Actions
            </button>
          </div>
        ) : (
          actions.map(action => {
            const TypeIcon = getTypeIcon(action.type);
            const typeColor = getTypeColor(action.type);
            const priorityConfig = getPriorityConfig(action.priority);
            
            return (
              <div
                key={action.action_id}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                data-testid={`action-${action.action_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-xl ${typeColor}`}>
                    <TypeIcon size={24} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${priorityConfig.bg} ${priorityConfig.text}`}>
                        {priorityConfig.label}
                      </span>
                      <span className="text-xs text-slate-500 capitalize">
                        {action.type.replace(/_/g, ' ')}
                      </span>
                      {action.status === 'in_progress' && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                          In Progress
                        </span>
                      )}
                      {action.metadata?.auto_generated && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                          <Sparkles size={10} /> AI Generated
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 mb-1">{action.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{action.description}</p>
                    
                    {/* Suggested Actions */}
                    {action.suggested_actions && action.suggested_actions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {action.suggested_actions.map((sa, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleActionClick(action, sa)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg transition-colors"
                          >
                            {sa.label}
                            {sa.url && <ExternalLink size={12} />}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Metadata */}
                    {action.metadata && Object.keys(action.metadata).length > 0 && !action.metadata.auto_generated && (
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        {action.metadata.emissions_gap && (
                          <span className="flex items-center gap-1">
                            <Leaf size={12} />
                            Gap: {action.metadata.emissions_gap.toLocaleString()} tCO2e
                          </span>
                        )}
                        {action.metadata.pending_count && (
                          <span className="flex items-center gap-1">
                            <Users size={12} />
                            {action.metadata.pending_count} suppliers pending
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                      <span>Source: {action.source_module}</span>
                      {action.deadline && (
                        <span className="flex items-center gap-1 text-orange-600">
                          <Clock size={12} />
                          Due: {new Date(action.deadline).toLocaleDateString()}
                        </span>
                      )}
                      <span>Impact Score: {action.impact_score}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {action.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateActionStatus(action.action_id, 'start')}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                          title="Start"
                        >
                          <Play size={14} /> Start
                        </button>
                        <button
                          onClick={() => updateActionStatus(action.action_id, 'complete')}
                          className="flex items-center gap-1 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm"
                          title="Complete"
                        >
                          <Check size={14} /> Done
                        </button>
                        <button
                          onClick={() => updateActionStatus(action.action_id, 'dismiss')}
                          className="flex items-center gap-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                          title="Dismiss"
                        >
                          <X size={14} /> Dismiss
                        </button>
                      </>
                    )}
                    {action.status === 'in_progress' && (
                      <button
                        onClick={() => updateActionStatus(action.action_id, 'complete')}
                        className="flex items-center gap-1 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm"
                        title="Complete"
                      >
                        <Check size={14} /> Complete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActionCenter;
